import { taxonomyTagMatch } from './catalog-taxonomy';
import { isPlatformTag } from './platform-tags';
import { productTagsOf, type ProductTagFields } from './product-filters';

/**
 * A product's tags as the PUBLIC catalog sees them: the tag pages, the sitemap,
 * /browse's tag list, and every product page's breadcrumb and tag links.
 *
 * WHICH FIELD. A product document carries two tag-shaped fields and only one of
 * them is the seller's. `productTags` (or `details.productTags` on older
 * documents) is what the tag selector writes, and `productTagsOf` owns the
 * precedence between those two. The top-level `tags` is the RIBBON list
 * (featured, boosted, growing, discounted, secure), and both the create form
 * and the importer write it as `[]`. Every crawlable surface used to read
 * `tags`, so no tag page ever listed a product, the sitemap never carried one,
 * and no product page linked to one. Nothing errored: every tag page simply
 * answered "Tag not found".
 *
 * WHY THIS IS NOT IN public-catalog.ts. That module is `server-only`, so the
 * test runner cannot load it, and the part that went wrong unnoticed is exactly
 * the part that needs pinning. Everything here is pure.
 *
 * PLATFORM MARKERS. createDiscount and the discount sweep (functions/src/index.ts)
 * append "discounted" to `productTags` while a sale is live and strip it when the
 * sale ends (platform-tags.ts). That is a price state, not a topic: as a
 * crawlable page it would appear and vanish with every sale, and a product with
 * no other tag would get "Discounted" as its breadcrumb. So it is dropped here.
 * The marketplace's "discounted" filter does not read the stored marker either;
 * it asks whether the listing is on sale (filterTagsOf in product-filters.ts).
 *
 * TYPE AND PURPOSE WORDS. Since 2026-10-20 a word that names a catalogue type
 * or purpose ("templates", "marketing", "plr") is a field, not a tag
 * (catalog-taxonomy.ts), and the cleanup script moves such words off every
 * product (scripts/strip-taxonomy-tags.mjs). Until it has run, and for any
 * that slip back in, they are dropped here too, so no page lists them as tags
 * or links to /tag/{word}, which the middleware redirects to the marketplace's
 * view of that type or purpose.
 */

export type CatalogTag = {
  /** URL segment. Already lowercased and hyphenated. */
  slug: string;
  /** Human-readable form, taken from the most common spelling across products. */
  label: string;
  count: number;
};

/**
 * Normalise a tag into a URL segment. The tag selector already lowercases and
 * hyphenates on input, so for anything created through the UI this is the
 * identity function; it exists to keep legacy tags (created before that
 * normalisation, or seeded directly into Firestore) from minting a second URL
 * for a tag that already has one.
 */
export function tagSlug(tag: string): string {
  return tag
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Title-case a slug for display when we have no better spelling on record. */
export function tagLabel(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * The seller's tags on a product, as they were written, minus platform markers,
 * type and purpose words, and anything that cannot become a URL. The one way a
 * crawlable surface reads a product's tags.
 */
export function catalogTagsOf(doc: ProductTagFields): string[] {
  return productTagsOf(doc).filter((tag) => tagSlug(tag) !== '' && !isPlatformTag(tag) && taxonomyTagMatch(tag) === null);
}

/** The same tags as URL slugs, deduped, in the seller's order. */
export function catalogTagSlugs(doc: ProductTagFields): string[] {
  const slugs: string[] = [];
  for (const tag of catalogTagsOf(doc)) {
    const slug = tagSlug(tag);
    if (!slugs.includes(slug)) slugs.push(slug);
  }
  return slugs;
}

/**
 * The tag a product's breadcrumb goes through: its first. Shared by the JSON-LD
 * trail in the product layout and the visible breadcrumb in product-client.tsx,
 * which are two halves of one breadcrumb and must name the same crumb. They
 * used to compute it separately, and disagreed whenever the first tag had no
 * usable slug.
 */
export function primaryTagSlug(doc: ProductTagFields): string | null {
  return catalogTagSlugs(doc)[0] ?? null;
}

/**
 * Every tag in use across `products`, with how many products carry it, most
 * used first. Counted by slug, so "Line Icons" and "line-icons" are one tag, and
 * a product carrying both spellings counts once towards the indexing threshold.
 */
export function summariseTags(products: ReadonlyArray<{ tags: readonly string[] }>): CatalogTag[] {
  const counts = new Map<string, { spellings: Map<string, number>; count: number }>();

  for (const product of products) {
    // A product carrying the same tag twice (differently cased, say) must only
    // count once towards the indexing threshold.
    const seen = new Set<string>();
    for (const raw of product.tags) {
      const slug = tagSlug(raw);
      if (!slug || seen.has(slug)) continue;
      seen.add(slug);

      const entry = counts.get(slug) ?? { spellings: new Map<string, number>(), count: 0 };
      entry.count += 1;
      const spelling = raw.trim();
      entry.spellings.set(spelling, (entry.spellings.get(spelling) ?? 0) + 1);
      counts.set(slug, entry);
    }
  }

  return Array.from(counts.entries())
    .map(([slug, entry]): CatalogTag => {
      // Display the spelling sellers use most often; fall back to title-casing
      // the slug when the tag is only ever written in slug form.
      let label = tagLabel(slug);
      let best = 0;
      for (const [spelling, n] of entry.spellings) {
        if (n > best && spelling !== slug) {
          best = n;
          label = spelling;
        }
      }
      return { slug, label, count: entry.count };
    })
    .sort((a, b) => b.count - a.count || a.slug.localeCompare(b.slug));
}

/** The products carrying the tag `slug`, in the order given. */
export function productsTagged<P extends { tags: readonly string[] }>(
  products: readonly P[],
  slug: string,
): P[] {
  const normalized = tagSlug(slug);
  if (!normalized) return [];
  return products.filter((p) => p.tags.some((t) => tagSlug(t) === normalized));
}
